/**
 * Descripción legible de una propuesta (filas campo → valor). La usa la tarjeta del chat
 * y, en una línea, las opciones de las aclaraciones.
 */
import { currentVersion } from '../domain/model';
import type { AppData } from '../domain/data';
import { makeT, type TFn } from '../i18n/core';
import { en } from '../i18n/en';
import { es, type Messages } from '../i18n/es';
import { unitLabel } from '../ui/format';
import type { Proposal } from './interpreter';
import { dayNames, formatDay } from './normalize';

export interface DescribeI18n {
  t: TFn;
  m: Messages;
  locale: 'es' | 'en';
}

export const plainI18n = (locale: 'es' | 'en'): DescribeI18n => ({ t: makeT(locale), m: locale === 'en' ? en : es, locale });

type Schedule = Extract<Proposal, { kind: 'create_habit' }>['schedule'];

function frequency(s: Schedule, i: DescribeI18n): string {
  if (s.type === 'daily' || (s.type === 'weekdays' && s.days.length === 7)) return i.t('assistant.freq.daily');
  if (s.type === 'weekly_quota') return i.t('assistant.freq.quota', { count: s.times });
  return dayNames(s.days, i.locale);
}

export function describeProposal(p: Proposal, i: DescribeI18n, data: Pick<AppData, 'habits'>): [string, string][] {
  const { t, m, locale } = i;
  const date = (d: string | null) => (d ? formatDay(d, locale) : t('assistant.fields.noDate'));
  switch (p.kind) {
    case 'create_habit': {
      const goal = p.habitKind === 'quit' ? t('assistant.fields.max', { value: p.target, unit: unitLabel(m, p.unit, p.target) }) : p.goalType === 'numeric' ? `${p.target} ${unitLabel(m, p.unit, p.target)}` : t('assistant.fields.binaryGoal');
      const rows: [string, string][] = [
        [t('assistant.fields.name'), p.name],
        [t('assistant.fields.type'), t(`habit.kinds.${p.habitKind}`)],
        [t('assistant.fields.frequency'), frequency(p.schedule, i)],
        [t('assistant.fields.goal'), goal],
      ];
      rows.push([t('assistant.fields.reminder'), p.reminder ? t('chat.fields.reminderAt', { time: p.reminder.time }) : t('chat.fields.noSchedule')]);
      return rows;
    }
    case 'create_task':
      return [
        [t('assistant.fields.name'), p.name],
        [t('assistant.fields.date'), date(p.dueDate)],
        [t('assistant.fields.time'), p.dueTime ?? t('assistant.fields.noTime')],
        [t('assistant.fields.reminder'), p.reminder ? t('assistant.fields.reminderYes') : t('assistant.fields.reminderNo')],
      ];
    case 'add_to_list':
      return [
        [t('assistant.fields.list'), p.listId ? p.listName : t('assistant.fields.newList', { name: p.listName })],
        [t('assistant.fields.items'), p.items.join(', ')],
      ];
    case 'show_today':
      return [];
    case 'log_activity': {
      const h = data.habits.find((x) => x.id === p.habitId);
      const unit = p.unit ?? (h && h.kind !== 'task' ? currentVersion(h).unit : null);
      return [
        [t('assistant.fields.habit'), p.habitName],
        [t('assistant.fields.amount'), p.amount === null ? t('assistant.fields.markDone') : `${p.amount} ${unitLabel(m, unit, p.amount)}`],
        [t('assistant.fields.date'), date(p.date)],
      ];
    }
    case 'create_note': {
      const rows: [string, string][] = [
        [t('assistant.fields.category'), t(`brain.kinds.${p.noteKind}`)],
        [t('assistant.fields.text'), p.title],
      ];
      if (p.body) rows.push([t(p.noteKind === 'quote' ? 'brain.author' : 'brain.details'), p.body]);
      return rows;
    }
    case 'journal':
      return [[t('assistant.fields.text'), p.text.length > 120 ? `${p.text.slice(0, 120)}…` : p.text]];
    case 'start_focus':
      return [
        [t('assistant.fields.duration'), t('focus.minutes', { count: p.minutes })],
        [t('assistant.fields.habit'), (p.habitId && data.habits.find((h) => h.id === p.habitId)?.name) || p.label || t('focus.noLink')],
      ];
    case 'log_focus':
      return [
        [t('assistant.fields.duration'), t('focus.minutes', { count: p.minutes })],
        [t('assistant.fields.habit'), (p.habitId && data.habits.find((h) => h.id === p.habitId)?.name) || p.label || t('focus.noLink')],
        [t('assistant.fields.date'), date(p.date)],
      ];
    case 'update_habit': {
      const rows: [string, string][] = [[t('assistant.fields.habit'), p.habitName]];
      if (p.name !== undefined) rows.push([t('assistant.fields.name'), p.name]);
      if (p.schedule !== undefined) rows.push([t('assistant.fields.frequency'), frequency(p.schedule, i)]);
      if (p.target !== undefined) rows.push([t('assistant.fields.goal'), p.goalType === 'binary' ? t('assistant.fields.binaryGoal') : `${p.target} ${unitLabel(m, p.unit ?? null, p.target)}`]);
      rows.push([t('chat.fields.from'), t('chat.fields.fromToday')]);
      return rows;
    }
    case 'set_reminder':
      return [
        [t('assistant.fields.habit'), p.habitName],
        [t('assistant.fields.time'), p.time],
        [t('chat.fields.days'), p.days ? dayNames(p.days, locale) : t('chat.fields.scheduledDays')],
      ];
    case 'reschedule_task':
      return [
        [t('chat.fields.task'), p.habitName],
        [t('assistant.fields.date'), date(p.dueDate)],
        [t('assistant.fields.time'), p.dueTime ?? t('assistant.fields.noTime')],
      ];
    case 'archive_habit':
    case 'delete_habit':
      return [[t('assistant.fields.habit'), p.habitName]];
  }
}

/** Una línea: "Crear tarea · Estudiar · martes, 20 de octubre · 18:00". */
export function summarizeProposal(p: Proposal, i: DescribeI18n, data: Pick<AppData, 'habits'>): string {
  return [i.t(`assistant.kinds.${p.kind}`), ...describeProposal(p, i, data).map(([, v]) => v)].filter(Boolean).join(' · ');
}
